import React, { useEffect, useMemo, useRef, useState } from 'react';
import ConsumerMapProps, {
  CityLocationFeature,
  CityLocationGeoJSON,
  CityLocationProperties,
  DistrictLocationFeature,
  DistrictLocationGeoJSON,
  DistrictLocationProperties,
  StyleConfig,
} from './iConsumerMap';
import { LatLngBounds } from 'leaflet';
import { MapContainer, TileLayer, GeoJSON, useMap, LayersControl, Pane } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import fetcher from '../../helpers/fetcher';
import { ENVIRONMENT_CONFIG } from '../../constants/environmentConfig';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';
import BasicCard from '../basicCard/BasicCard';
import { useTranslation } from 'react-i18next';
const FitBounds: React.FC<{ bounds: LatLngBounds }> = ({ bounds }) => {
  const map = useMap();

  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [map, bounds]);

  return null;
};

const ConsumerMap: React.FC<ConsumerMapProps> = ({
  cityStyleFunction,
  districtStyleFunction,
  districtBorderStyleFunction,
  popupContentFunction,
  onFeatureClick,
  onFeatureHover,
  onFeatureHoverOut,
  center = [15.2, -100.2],
  defaultZoom = 12,
  maxZoom = 10,
  minZoom = 7.5,
  showTileLayer = false,
  showCityLayer = true,
  showDistrictLayer = true,
  showDistrictBorderLayer = true,
  defaultLayer = 'district',
  className = '',
  height = '600px',
  department,
  municipality,
  userLocation,
  triggeredFrom,
}) => {
  const [cityGeoData, setCityGeoData] = useState<CityLocationGeoJSON | null>(null);
  const [districtGeoData, setDistrictGeoData] = useState<DistrictLocationGeoJSON | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const geoJsonRef = useRef<any>(null);
  const mapRef = useRef<L.Map | null>(null);

  const { t } = useTranslation();

  // Default style functions
  const defaultCityStyle = (): StyleConfig => ({
    fillColor: '#999999',
    weight: 1.5,
    opacity: 1,
    color: '#EEEEEE',
    fillOpacity: 0.5,
  });

  const defaultDistrictStyle = (): StyleConfig => ({
    fillColor: '#999999',
    weight: 1.5,
    opacity: 1,
    color: '#FFFFFF',
    fillOpacity: 0.5,
  });

  const defaultDistrictBorderStyle = (): StyleConfig => ({
    fillOpacity: 0,
    weight: 2,
    opacity: 1,
    color: '#FFFFFF',
  });

  const defaultPopupContent = (feature: CityLocationFeature | DistrictLocationFeature): string => {
    if ('city' in feature.properties) {
      const props = feature.properties as CityLocationProperties;
      return `
        <div>
          <h3 class="text-2xl font-semibold">${props.city}</h3>
          <p><strong>Departamento:</strong> ${props.district}</p>
        </div>
      `;
    } else {
      const props = feature.properties as DistrictLocationProperties;
      return `
        <div>
          <h3 class="text-2xl font-semibold">${props.district}</h3>
          <p><strong>Country:</strong> ${props.country || 'N/A'}</p>
        </div>
      `;
    }
  };

  const fetchCityData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetcher.get(
        API_ENDPOINTS.LOCATION.GET_GEOJSON,
        {},
        {},
        ENVIRONMENT_CONFIG.CONSUMER_PRICE_SERVICE
      );

      if (response.data) {
        setCityGeoData(response.data);
      } else {
        throw new Error('No data received from API');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'City Data Fetching Failed!';
      setError(errorMessage);
      console.error('Map data fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDistrictData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetcher.get(
        API_ENDPOINTS.LOCATION.GET_DISTRICTS_GEOJSON,
        {},
        {},
        ENVIRONMENT_CONFIG.CONSUMER_PRICE_SERVICE
      );

      if (response.data) {
        setDistrictGeoData(response.data);
      } else {
        throw new Error('No data received from API');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'District Data Fetching Failed!';
      setError(errorMessage);
      console.error('Map data fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.eachLayer((layer: any) => {
        const popupContent = popupContentFunction?.(
          layer.feature,
          department,
          municipality,
          userLocation
        );
        layer.setPopupContent(popupContent);
      });
    }
  }, [department, municipality, userLocation]);

  useEffect(() => {
    if (showCityLayer) fetchCityData();
    if (showDistrictLayer || showDistrictBorderLayer) fetchDistrictData();
  }, [showCityLayer, showDistrictLayer, showDistrictBorderLayer]);

  const cleanedDistrictGeoData = useMemo(() => {
    if (!districtGeoData) return null;

    const clonedData: DistrictLocationGeoJSON = JSON.parse(JSON.stringify(districtGeoData));

    const removeHoles = (polygonCoords: any[]) => {
      return [polygonCoords[0]];
    };

    clonedData.features.forEach((feature) => {
      if (feature.geometry.type === 'Polygon') {
        feature.geometry.coordinates = removeHoles(feature.geometry.coordinates);
      } else if (feature.geometry.type === 'MultiPolygon') {
        feature.geometry.coordinates = feature.geometry.coordinates.map(removeHoles);
      }
    });

    return clonedData;
  }, [districtGeoData]);

  const mapBounds = cityGeoData
    ? new LatLngBounds(
        [cityGeoData.bounds.minY - 0.5, cityGeoData.bounds.minX - 0.1],
        [cityGeoData.bounds.maxY - 0.5, cityGeoData.bounds.maxX + 0.1]
      )
    : null;

  const onEachFeature = (feature: CityLocationFeature | DistrictLocationFeature, layer: any) => {
    const popupContent = popupContentFunction
      ? popupContentFunction(feature, department, municipality, userLocation)
      : defaultPopupContent(feature);

    layer.bindPopup(popupContent);

    layer.on({
      mouseover: (e: any) => {
        const layer = e.target;
        layer.setStyle({
          weight: 3,
          // color: '#004CD350',
          fillOpacity: 0.7,
        });

        // if (onFeatureHover) {
        //   onFeatureHover(feature, layer);
        // }
      },
      mouseout: (e: any) => {
        const layer = e.target;
        layer.setStyle({
          weight: 1.5,
          fillOpacity: 0.5,
        });
        // if (feature.properties.hasOwnProperty('city')) {
        //   layer.setStyle(
        //     cityStyleFunction ? cityStyleFunction(feature as CityLocationFeature) : defaultCityStyle
        //   );
        // } else {
        //   layer.setStyle(
        //     districtStyleFunction
        //       ? districtStyleFunction(feature as DistrictLocationFeature)
        //       : defaultDistrictStyle
        //   );
        // }

        // if (onFeatureHoverOut) {
        //   onFeatureHoverOut(feature, e.target);
        // }
      },
      click: (e: any) => {
        if (triggeredFrom === 'alertMap') {
          let alertContent = popupContentFunction?.(
            feature,
            department,
            municipality,
            userLocation
          );
          layer.bindPopup(alertContent);
        } else {
          popupContentFunction?.(feature, department, municipality, userLocation);
        }

        if (onFeatureClick) {
          onFeatureClick(feature, layer);
        }
      },
    });
  };

  if (loading) return <div className="p-4">{t('loadingMapData')}</div>;
  if (error)
    return (
      <div className="h-52 p-4 text-center text-3xl">
        {t('mapDataLoadFail')} <br />
        {t('checkInternet')}
      </div>
    );
  if (!cityGeoData && !districtGeoData)
    return (
      <div className="flex h-52 flex-col items-center justify-center p-4 text-center">
        <div className="mb-2 text-2xl text-gray-600">🗺️</div>
        <div className="mb-2 text-lg text-gray-600">{t('mapDataNotAvailable')}</div>
        <div className="text-sm text-gray-500">{t('geographicDataNeeded')}</div>
      </div>
    );

  return (
    <div className="mt-1 w-full bg-white py-7" style={{ height }}>
      <BasicCard radius="lg">
        <MapContainer
          style={{ height: '100%', width: '100%', backgroundColor: 'white' }}
          center={center}
          zoom={defaultZoom}
          maxZoom={maxZoom}
          minZoom={minZoom}
          zoomSnap={0.1}
          scrollWheelZoom={true}
          className={`map-container rounded-xl border shadow-md ${className}`}
          maxBounds={mapBounds || undefined}
          maxBoundsViscosity={1.0}
          ref={mapRef}
        >
          {showTileLayer && (
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
          )}

          <LayersControl>
            {showDistrictLayer && (
              <LayersControl.BaseLayer checked={defaultLayer === 'district'} name="Departments">
                {cleanedDistrictGeoData && (
                  <GeoJSON
                    key="district-layer"
                    data={cleanedDistrictGeoData}
                    style={districtStyleFunction || defaultDistrictStyle}
                    onEachFeature={onEachFeature}
                  />
                )}
              </LayersControl.BaseLayer>
            )}
            {showCityLayer && (
              <LayersControl.BaseLayer checked={defaultLayer === 'city'} name="Municipalities">
                {cityGeoData && (
                  <GeoJSON
                    ref={geoJsonRef}
                    data={cityGeoData}
                    style={cityStyleFunction || defaultCityStyle}
                    onEachFeature={onEachFeature}
                  />
                )}
                {showDistrictBorderLayer && cleanedDistrictGeoData && (
                  <LayersControl.Overlay checked={false} name={t('districtBorders')}>
                    <Pane name="districtBordersPane" style={{ zIndex: 699 }}>
                      <GeoJSON
                        key="district-border-layer"
                        data={cleanedDistrictGeoData}
                        style={districtBorderStyleFunction || defaultDistrictBorderStyle}
                        interactive={false}
                      />
                    </Pane>
                  </LayersControl.Overlay>
                )}
              </LayersControl.BaseLayer>
            )}
          </LayersControl>

          {mapBounds && <FitBounds bounds={mapBounds} />}
        </MapContainer>
      </BasicCard>
    </div>
  );
};

export default ConsumerMap;
